import {
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  EsriPoint,
  EsriExpand,
  EsriSimpleMarkerSymbol,
  EsriGraphic
} from '../../../config/esri.module';
import { MainMapService } from '../../../shared/service/main-map.service';
import { DomUtilService } from '../../../shared/service/dom-util.service';
import { WidgetNotifyService } from '../../../shared/service/widget-notify.service';
import { BaseWidget } from '../base-widget/base-widget';
import {CONSTANTS_VALUE} from "../../../config/constants.module";
import {MessageService} from "../../../core/service/message.service";
import {ConfigService} from "../../../config/config.service";

@Component({
    selector: 'go-to',
    templateUrl: './go-to.component.html',
    styleUrls: ['go-to.component.scss'],
    standalone: false
})
export class GoToComponent extends BaseWidget implements OnInit, AfterViewInit {
  @ViewChild('goToComponentRef', { static: true }) goToComponentRef: ElementRef;
  @ViewChild('xCoordinateRef', { static: true }) xCoordinateRef: ElementRef;
  @ViewChild('yCoordinateRef', { static: true }) yCoordinateRef: ElementRef;

  public formGroup: UntypedFormGroup;

  constructor(
    public mainMapService: MainMapService,
    public widgetNotifyService: WidgetNotifyService,
    private _domUtilService: DomUtilService,
    private _translate: TranslateService,
    private messageService: MessageService,
    private configService: ConfigService
  ) {
    super(mainMapService, widgetNotifyService);
  }

  ngOnInit() {
    const mapView: __esri.MapView = this.mainMapService.mapView;

    // Create an expand instance and set the content
    const expandProperties: __esri.ExpandProperties = {
      view: mapView,
      id: 'GoToComponent',
      content: this.goToComponentRef.nativeElement,
      expandIcon: 'applications',
    };
    this.expandWidget = new EsriExpand(expandProperties);
    // translate tooltip icon
    super.translateToolTipWidgetIcon(this.expandWidget, this._translate, 'WIDGET.GOTO.TOOLTIP');
    // add widget to the main map
    mapView.ui.add(this.expandWidget, 'top-right');
    this.initWidgetListeners();

    // create controls
    this.formGroup = new UntypedFormGroup({
      xCoordinate: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{7,7}$'),
      ]),
      yCoordinate: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{7,7}$'),
      ]),
    });
  }

  ngAfterViewInit() {
    // listen on language change
    this._translate.onLangChange.subscribe(() => {
      super.translateToolTipWidgetIcon(this.expandWidget, this._translate, 'WIDGET.GOTO.TOOLTIP');
    });
  }


  /**
   * @name panTo
   * @description Pans to x y on the map
   */
  public panTo() {
    this.validateXY();
    if (this.formGroup.valid) {
      this.panToPoint(this.getPoint());
    }
  }

  /**
   * @name goTo
   * @description Zooms to x y on the map
   */
  public goTo() {
    this.validateXY();
    if (this.formGroup.valid) {
      this.goToPoint(this.getPoint());
    }
  }

  /**
   * @name flash
   * @description Flashes the x y on the map
   */
  public flash() {
    this.validateXY();
    if (this.formGroup.valid) {
      this.flashPoint(this.getPoint());
    }
  }

  /**
   * @name getPoint
   * @returns __esri.Point
   * @description Creates point from x y
   */
  private getPoint(): __esri.Point {
    return new EsriPoint({
      latitude: this.formGroup.controls.yCoordinate.value,
      longitude: this.formGroup.controls.xCoordinate.value,
      spatialReference: this.mainMapService.mapView.spatialReference,
    });
  }

  /**
   * @name validateXY
   * @description Validates x y input
   */
  private validateXY(): void {
    // validate x-coordinate
    if (this.formGroup.controls.xCoordinate.valid) {
      this._domUtilService.unSetFieldInvalidBootstrapCSSFlag(this.xCoordinateRef);
    } else {
      // required invalid
      if (this.formGroup.controls.xCoordinate.errors.required) {
        this._translate.get('WIDGET.GOTO.VALIDATION.REQUIRED').subscribe(msg => {
          this._domUtilService.setFieldInvalidBootstrapCSSFlag(this.xCoordinateRef, msg);
        });
      }
      // length invalid
      if (this.formGroup.controls.xCoordinate.errors.pattern) {
        this._translate.get('WIDGET.GOTO.VALIDATION.LENGTH').subscribe(msg => {
          this._domUtilService.setFieldInvalidBootstrapCSSFlag(this.xCoordinateRef, msg);
        });
      }
    }

    // validate x-coordinate
    if (this.formGroup.controls.yCoordinate.valid) {
      this._domUtilService.unSetFieldInvalidBootstrapCSSFlag(this.yCoordinateRef);
    } else {
      // required invalid
      if (this.formGroup.controls.yCoordinate.errors.required) {
        this._translate.get('WIDGET.GOTO.VALIDATION.REQUIRED').subscribe(msg => {
          this._domUtilService.setFieldInvalidBootstrapCSSFlag(this.yCoordinateRef, msg);
        });
      }
      // length invalid
      if (this.formGroup.controls.yCoordinate.errors.pattern) {
        this._translate.get('WIDGET.GOTO.VALIDATION.LENGTH').subscribe(msg => {
          this._domUtilService.setFieldInvalidBootstrapCSSFlag(this.yCoordinateRef, msg);
        });
      }
    }
  }
  public destroySelf() {
   super.destroySelf();
    this.mainMapService.mapView.graphics.removeAll();
  }

  /**
   * @name panTo
   * @param point
   * @description Pans to point and flashes it
   */
  private panToPoint(point: __esri.Point) {
    this.mainMapService.mapView
      .goTo(point)
      .then(() => {
        this.flashPoint(point);
      })
      .catch(() => {
        this.messageService.error('NOTIFICATIONS.ERROR.MAP.PAN');
      });
  }

  /**
   * @name flashPoint
   * @param point
   * @description Flashes point
   */
  private flashPoint(point: __esri.Point) {
    // create flash graphic
    const symbol: __esri.SimpleMarkerSymbol = new EsriSimpleMarkerSymbol(
      this.configService.mainMapConfig.goTo.flash.symbol
    );
    const graphicProperties: __esri.GraphicProperties = {
      geometry: point,
      symbol: symbol,
      attributes: {
        name: CONSTANTS_VALUE.GRAPHIC_TYPE.FLASH_POINT,
      },
    };
    const flashGraphic = new EsriGraphic(graphicProperties);
    this.mainMapService.mapView.graphics.add(flashGraphic);

    // remove FlashPoint graphic after 20 seconds from the mapview
    setTimeout(() => {
      this.mainMapService.mapView.graphics.remove(flashGraphic);
    }, CONSTANTS_VALUE.SEARCH_GRAPHIC_TIMEOUT.MINUTES_2);
  }

  /**
   * @name goTo
   * @param point
   * @description Zooms to point and flashes it
   */
  private goToPoint(point: __esri.Point) {
    const target = {
      target: point,
      scale: this.configService.mainMapConfig.goTo.scale,
    };

    this.mainMapService.mapView
      .goTo(target)
      .then(() => {
        this.flashPoint(point);
      })
      .catch(() => {
        this.messageService.error('NOTIFICATIONS.ERROR.MAP.GOTO');
      });
  }
}
